import React, { useEffect, useState } from 'react';
import { Show } from '@refinedev/antd';
import { useShow, useUpdate } from '@refinedev/core';
import { Button, Col, Image, Input, Row, Select, Space, Tag, theme } from 'antd';
import { AdminFeedbackReportDto, UpdateFeedbackReportDto } from '@wellverse/shared-ts';
import { Field } from '../../components/Field';
import { API_BASE_URL, axiosInstance } from '../../providers/dataProvider';
import { optionLabel, PRIORITY_OPTIONS, STATUS_OPTIONS } from './labels';

/**
 * Screenshots are private: the endpoint needs the CMS token, so it is fetched
 * as a blob instead of being linked from an <img src>.
 */
const useScreenshotUrl = (id: string | undefined, hasScreenshot: boolean) => {
  const [url, setUrl] = useState<string>();
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!id || !hasScreenshot) return;
    let objectUrl: string | undefined;
    let cancelled = false;
    axiosInstance
      .get(`${API_BASE_URL}/feedback-report-admin/reports/${id}/screenshot`, { responseType: 'blob' })
      .then((res) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(res.data);
        setUrl(objectUrl);
      })
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [id, hasScreenshot]);

  return { url, failed };
};

export const FeedbackReportShow: React.FC = () => {
  const { token } = theme.useToken();
  const { queryResult } = useShow<AdminFeedbackReportDto>({ resource: 'feedback-reports' });
  const item = queryResult?.data?.data;
  const { url: screenshotUrl, failed: screenshotFailed } = useScreenshotUrl(
    item?.id,
    item?.hasScreenshot ?? false,
  );

  const { mutate: update, isLoading: saving } = useUpdate();
  const [triage, setTriage] = useState<UpdateFeedbackReportDto>({});

  useEffect(() => {
    if (item) {
      setTriage({ priority: item.priority, status: item.status, internalNotes: item.internalNotes ?? '' });
    }
  }, [item]);

  if (!item) {
    return <div style={{ color: token.colorTextSecondary }}>Loading...</div>;
  }

  const dirty =
    triage.priority !== item.priority ||
    triage.status !== item.status ||
    (triage.internalNotes ?? '') !== (item.internalNotes ?? '');

  const save = () =>
    update({
      resource: 'feedback-reports',
      id: item.id,
      values: triage,
      successNotification: { type: 'success', message: 'Report updated' },
    });

  return (
    <Show resource="feedback-reports" title={item.reportId}>
      <Row gutter={32}>
        <Col xs={24} md={8}>
          <Field label="Screenshot">
            {!item.hasScreenshot ? (
              <span style={{ color: token.colorTextSecondary }}>No image attached</span>
            ) : screenshotUrl ? (
              <Image src={screenshotUrl} style={{ maxWidth: '100%', borderRadius: 8 }} />
            ) : (
              <span style={{ color: token.colorTextSecondary }}>
                {screenshotFailed ? 'Could not load the screenshot' : 'Loading...'}
              </span>
            )}
          </Field>
          {item.hasScreenshot && (
            <Field label="Screenshot source">{optionLabel(item.screenshotSource)}</Field>
          )}
        </Col>

        <Col xs={24} md={8}>
          <Field label="Report ID">{item.reportId}</Field>
          <Field label="Category">{optionLabel(item.category)}</Field>
          {item.detailId && <Field label="What happened">{optionLabel(item.detailId)}</Field>}
          {item.aiArea && <Field label="AI area">{optionLabel(item.aiArea)}</Field>}
          {item.aiDetailIds.length > 0 && (
            <Field label="What was wrong">
              <Space wrap>
                {item.aiDetailIds.map((id) => (
                  <Tag key={id} color={id === 'ai_unsafe' ? 'red' : undefined}>
                    {optionLabel(id)}
                  </Tag>
                ))}
              </Space>
            </Field>
          )}
          {item.actualValue && <Field label="What it actually was">{item.actualValue}</Field>}
          <Field label="Severity">{optionLabel(item.severity)}</Field>
          <Field label="Description">
            <div style={{ whiteSpace: 'pre-wrap' }}>{item.description || '—'}</div>
          </Field>

          <Field label="Tester">
            {item.userName ? `${item.userName} · ` : ''}
            <code>{item.userId}</code>
          </Field>
          <Field label="Report started">{new Date(item.triggerAt).toLocaleString()}</Field>
          {item.capturedAt && (
            <Field label="Captured by app">{new Date(item.capturedAt).toLocaleString()}</Field>
          )}
          <Field label="App version">
            {item.appVersion} ({item.buildNumber})
          </Field>
          <Field label="Device">
            {item.deviceModel} · {item.osVersion}
          </Field>
          <Field label="Source">{item.source}</Field>
          <Field label="Received">{new Date(item.createdAt).toLocaleString()}</Field>
        </Col>

        <Col xs={24} md={8}>
          <Field label="Priority">
            <Select
              style={{ width: 160 }}
              value={triage.priority}
              options={PRIORITY_OPTIONS.map((p) => ({ value: p, label: p }))}
              onChange={(priority) => setTriage((t) => ({ ...t, priority }))}
            />
          </Field>
          <Field label="Status">
            <Select
              style={{ width: 160 }}
              value={triage.status}
              options={STATUS_OPTIONS}
              onChange={(status) => setTriage((t) => ({ ...t, status }))}
            />
          </Field>
          <Field label="Internal notes">
            <Input.TextArea
              rows={6}
              value={triage.internalNotes}
              onChange={(e) => setTriage((t) => ({ ...t, internalNotes: e.target.value }))}
            />
          </Field>
          <Button type="primary" onClick={save} loading={saving} disabled={!dirty}>
            Save
          </Button>
          <Field label="Last updated">{new Date(item.updatedAt).toLocaleString()}</Field>
        </Col>
      </Row>
    </Show>
  );
};
